import React, { useEffect, useMemo } from 'react'

import { Box, TablePagination, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

import Table from '../../../components/Table/Table'
import { CommonTableColumnsEnum } from '../../../components/Table/types'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { SortDirectionCapital } from '../../../types'
import { getTablePaginationDisplayedRows } from '../../../utils'
import { EmptyData } from '../../Reports'
import {
  fetchOpenPositionsData,
  updateOpenPositionsOrder,
  updateOpenPositionsPage,
  updateOpenPositionsPageSize,
  updateOpenPosOrderBy,
  updateOpenSortOrder,
} from '../DashboardSlice'
import { OpenPositions } from '../types'

export const OpenPositionsTable = () => {
  const { t } = useTranslation()

  const { spacing } = useTheme()
  const dispatch = useAppDispatch()

  const {
    data,
    pagination: { columns, page, perPage, itemCount, order, orderBy },
  } = useAppSelector((state) => state.dashboard.positions.open)

  const { currentAccount, period } = useAppSelector((state) => state.userAccounts)

  const [startDate, endDate] = period
  const searchPeriod = useMemo(
    () => (startDate && endDate ? [startDate, endDate] : [null, null]),
    [startDate, endDate],
  )

  const handlePageChange = (e: unknown, newPage: number) => {
    dispatch(updateOpenPositionsPage(newPage + 1))
  }
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateOpenPositionsPageSize(parseInt(e.target.value, 10)))
    dispatch(updateOpenPositionsPage(1))
  }

  const handleColumnReorder = (startIndex: number, endIndex: number) => {
    const newColumnOrder = Array.from(columns)
    const [removed] = newColumnOrder.splice(startIndex, 1)
    newColumnOrder.splice(endIndex, 0, removed)
    dispatch(updateOpenPositionsOrder(newColumnOrder))
  }

  const handleSort = (sortColumn: keyof OpenPositions) => {
    if (orderBy === sortColumn) {
      dispatch(
        updateOpenSortOrder(
          order === SortDirectionCapital.asc ? SortDirectionCapital.desc : SortDirectionCapital.asc,
        ),
      )
    } else {
      dispatch(updateOpenPosOrderBy(sortColumn))
    }
  }

  useEffect(() => {
    if (currentAccount?.id) {
      dispatch(
        fetchOpenPositionsData({
          order,
          orderBy: orderBy || CommonTableColumnsEnum.id,
          page,
          perPage,
          period: searchPeriod,
          account: currentAccount.id,
        }),
      )
    }
  }, [dispatch, page, perPage, searchPeriod, currentAccount?.id, order, orderBy])

  return data.length ? (
    <>
      <Table
        columnOrder={columns}
        sortingColumn={orderBy}
        sortingDirection={order}
        onColumnReorder={handleColumnReorder}
        onSort={handleSort}
        displayedTrades={data}
        showTradeInfoColumn={false}
      />
      <TablePagination
        labelRowsPerPage={t('perPage', 'Per page')}
        labelDisplayedRows={getTablePaginationDisplayedRows(t)}
        rowsPerPageOptions={[5, 10, 20, 50, 100]}
        component="div"
        count={itemCount}
        rowsPerPage={perPage}
        page={page - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
      />
    </>
  ) : (
    <Box mt={4} height={spacing(30)}>
      <EmptyData />
    </Box>
  )
}
